import React from "react";
import { Section } from "../../../components/section";
import { SectionHeader } from "../../../components/section-header";
import { ParagraphGroup } from "../../../components/paragraph-group";
import { MethodOverview } from "../../../components/method-overview";
import { InsightOverview } from "../../../components/insight-overview";
import { FullWidthImage } from "../../../components/full-width-image";
import Speedometer from "../../../images/speedometer.gif";
import Scroll from "../../../images/scroll.gif";
import Engagement from "../../../images/Engagement.png";

export interface INormsProps {
    sectionId: string;
    index: number;
}

export function Norms(props: INormsProps) {
    const formattedIndex = props.index > 10 ? props.index : `0${props.index}`;
    return (
        <Section
            sectionId={props.sectionId}
            isLast={false}
        >
            <SectionHeader
                title={`${formattedIndex} Revelation`}
            />
            <ParagraphGroup
                body={[
                    "To understand the interplay between user behaviors and misinformation consumption and spread, we conducted think-aloud testing to learn how users experience social platforms."
                ]}
            />
            <MethodOverview
                methods={
                    [
                        {
                            title: "Think-Aloud Testing",
                            body: "We observed how imposed users interacted with their feed and evaluated the information and accounts they came across",
                            imgSrc: Scroll,
                            altText: "Gif of someone scrolling on their Twitter feed"
                        }
                    ]
                }
            />
            <InsightOverview
                insights={
                    [
                        {
                            title: "People Are Well-Intentioned But Vulnerable",
                            body: "Most people don't want to consume or spread misinformation, but psychological and situational factors leave them vulnerable.",

                        },
                        {
                            title: "Users Are Unaware of Their Own Damaging Behaviors",
                            body: "While users are increasingly aware of the negative impact platforms can have on themselves and society, they do not recognize how their own behavior also contributes to the problem."
                        }
                    ]
                }
            />
            <ParagraphGroup
                body={
                    [
                        "Our research participants reflected the broader landscape of social platforms: most people don't want to spread misinformation but factors such as fatigue, bias, and identity can make people more likely to jump to quick judgments that align with their own viewpoints, rather than taking the time to evaluate information critically. This problem is compounded by the speed and quantity of information intake on social platforms. Because of how prevalent this problem was in our research, this group became our target audience."
                    ]
                }
            />
            <ParagraphGroup
                title="The Engagement Model"
                body={
                    [
                        "We distilled our research findings up to this point into the User Engagement Model. This model captures how different factors shift user engagement as they interact with content on social platforms, making them either more or less vulnerable to misinformation."
                    ]
                }
            />
            <FullWidthImage
                imgSrc={Engagement}
                altText="A model showing the different levels of web content engagement—creating, foraging, and grazing."
            />
            <FullWidthImage
                imgSrc={Speedometer}
                altText="A model showing the different levels of web content engagement—creating, foraging, and grazing."
            />
            <ParagraphGroup
                body={
                    [
                        "Engagement is fluid, and interest and motivation cause users to move between different levels of engagement. For example, users are less likely to be critically evaluating sources and credibility during the grazing phase. But if they come across a topic of interest, then they may move into the foraging phase, where they'll be more likely to actively seek out or evaluate information"
                    ]
                }
            />
        </Section>
    );
}
